import { endpoints, packed, types } from "magnetar-common";
import { globalEvents } from "@/event-bus";
import * as misskey from "calckey-js";
import * as Acct from "calckey-js/built/acct";
import { magFormatAcct } from "@/scripts-mag/mag-util";
import { legacyApi, magApi } from "@/api-client";

export type UserId = Pick<packed.PackUserBase, "id">;

export async function acceptFollow(user: UserId) {
    return await legacyApi("following/requests/accept", {
        userId: user.id,
    }).then(() => {
        globalEvents.emit("followeeProcessed", user);
    });
}

export async function rejectFollow(user: UserId) {
    return await legacyApi("following/requests/reject", {
        userId: user.id,
    }).then(() => {
        globalEvents.emit("followeeProcessed", user);
    });
}

export const UserModel = Object.assign(
    function <T extends packed.PackUserBase | misskey.entities.User>(user: T) {
        return {
            get inner(): T {
                return user;
            },
            get username(): string {
                return user.username;
            },
            get host(): string | null {
                return user.host;
            },
            get localUrl() {
                return `/users/${user.id}`;
            },
            get userPageUrl() {
                return `/@${magFormatAcct(user)}`;
            },
            get userInfoPageUrl() {
                return `/user-info/${user.id}`;
            },
            async follow() {
                return legacyApi("following/create", {
                    userId: user.id,
                });
            },
            async deleteFollow() {
                return legacyApi("following/delete", {
                    userId: user.id,
                });
            },
        };
    },
    {
        getByAcct: async (
            acct:
                | (Pick<Acct.Acct, "username"> &
                      Partial<Pick<Acct.Acct, "host">>)
                | string,
            options: types.UserByIdReq = {},
        ) => {
            return await magApi(endpoints.GetUserByAcct, options, {
                user_acct:
                    typeof acct === "string"
                        ? acct
                        : Acct.toString({
                              username: acct.username,
                              host: acct.host ?? null,
                          }),
            }).then((r) => (r ? UserModel(r as packed.PackUserBase) : null));
        },
        getById: async (userId: string, options: types.UserByIdReq = {}) => {
            return await magApi(endpoints.GetUserById, options, {
                user_id: userId,
            }).then((r) => (r ? UserModel(r as packed.PackUserBase) : null));
        },
    },
);
